import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '@shared/lib/supabase';

export interface MerchantContextValue {
  solanaWalletAddress: string | null;
  merchantSettings: Record<string, unknown> | null;
  merchantLoaded: boolean;
  refreshMerchant: () => Promise<void>;
}

export const MerchantContext = createContext<MerchantContextValue>({
  solanaWalletAddress: null,
  merchantSettings: null,
  merchantLoaded: false,
  refreshMerchant: async () => {},
});

export function useMerchantProvider(user: User | null): MerchantContextValue {
  const [solanaWalletAddress, setSolanaWalletAddress] = useState<string | null>(null);
  const [merchantSettings, setMerchantSettings] = useState<Record<string, unknown> | null>(null);
  const [merchantLoaded, setMerchantLoaded] = useState(false);

  const refreshMerchant = useCallback(async () => {
    if (!user) {
      setSolanaWalletAddress(null);
      setMerchantSettings(null);
      setMerchantLoaded(true);
      return;
    }
    const { data } = await supabase
      .from('merchants')
      .select('solana_wallet_address, settings')
      .eq('id', user.id)
      .maybeSingle();
    const addr = (data?.solana_wallet_address as string | null | undefined) ?? null;
    setSolanaWalletAddress(addr && addr.length > 0 ? addr : null);
    setMerchantSettings((data?.settings as Record<string, unknown> | undefined) ?? null);
    setMerchantLoaded(true);
  }, [user]);

  useEffect(() => {
    setMerchantLoaded(false);
    refreshMerchant();
  }, [refreshMerchant]);

  return { solanaWalletAddress, merchantSettings, merchantLoaded, refreshMerchant };
}

export function useMerchant(): MerchantContextValue {
  return useContext(MerchantContext);
}
